/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 본문 블록을 정의한다.
 *
 * @file /src/rootin/blocks/paragraph/Paragraph.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 9.
 */
import { Block } from '@rootin/base/Block';
import { Element } from '@rootin/base/Element';
import { Editor } from '@rootin/core/Editor';
import { Text } from '@rootin/elements/text/Text';
import { Language, LanguageText } from '@utils/Language';
import './Paragraph.css';

export interface Properties {
    /**
     * CSS/구조 마커용 역할
     */
    role?: string;

    /**
     * 내용이 비어있을 때 표시할 placeholder 텍스트
     */
    placeholder?: LanguageText;
}

export class Paragraph extends Block {
    /**
     * 블록타입
     */
    static type = 'paragraph';

    /**
     * 블록종류
     */
    static kind: 'inline' | 'atomic' = 'inline';

    /**
     * 블록아이콘
     */
    static icon: string =
        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-text-initial-icon lucide-text-initial"><path d="M15 5h6"/><path d="M15 12h6"/><path d="M3 19h18"/><path d="m3 12 3.553-7.724a.5.5 0 0 1 .894 0L11 12"/><path d="M3.92 10h6.16"/></svg>';

    /**
     * 블록명
     */
    static label: LanguageText = Language.get('rootin.blocks.paragraph.label');

    /**
     * 내용이 비어있을 때 표시할 placeholder 텍스트
     */
    placeholder: LanguageText = null;

    /**
     * 텍스트 엘리먼트
     */
    text: Text;

    /**
     * 본문 블록을 정의한다.
     *
     * @param editor - 이 블록이 속한 에디터
     * @param properties - 블록설정
     */
    constructor(editor: Editor, properties: Properties = null) {
        super(editor);
        this.placeholder = properties?.placeholder ?? Language.get('rootin.blocks.paragraph.placeholder');
        this.text = new Text(this, {
            name: 'text',
            role: properties?.role ?? 'text',
            placeholder: this.placeholder,
        });
        this.$getBlock().append(this.text.$getElement());
    }

    /**
     * 블록에 포커스를 지정한다.
     *
     * @param to_start - 포커스 위치 (TRUE : 처음, FALSE : 끝, NULL : 앵커)
     * @param from - 포커스 진입점
     */
    focus(to_start: boolean | null): void {
        if (to_start === null) {
            return;
        }
        this.text.focus(to_start);
    }

    /**
     * 블록을 렌더링한다.
     */
    async render(): Promise<void> {
        await this.text.render();
    }

    /**
     * 블록이 비었는지 확인한다.
     *
     * @returns 블록이 비었는지 여부
     */
    isEmpty(): boolean {
        return this.text.isEmpty();
    }

    /**
     * 블록값을 가져온다.
     * element 변수가 존재할 경우, 해당 엘리먼트의 기본값을 가져온다.
     *
     * @param element - 기본값을 가져오려는 엘리먼트 (NULL 인 경우 전체 블록데이터)
     * @returns 블록값
     */
    getValue(element: Element = null): any {
        if (element !== null) {
            return this.defaultValue?.[element.name] ?? null;
        }

        return { id: this.getId(), type: this.getType(), text: this.text.getValue() };
    }

    /**
     * 블록값을 지정한다.
     *
     * @param value - 블록값
     * @param is_default_value - 기본값 여부
     */
    async setValue(value: { [key: string]: any }, is_default_value: boolean = false): Promise<void> {
        if (is_default_value === true) {
            this.defaultValue = value;
        }

        await this.text.setValue(value.text);
    }

    /**
     * 블록의 HTML 을 받아, 블록값을 지정한다.
     *
     * @param html - 블록 HTML
     */
    async setValueFromHtml(html: string): Promise<void> {
        const dom = document.createElement('div');
        dom.innerHTML = html;

        const $rootinBlock = dom.firstElementChild;
        if (
            $rootinBlock === null ||
            $rootinBlock.tagName !== 'ROOTIN-BLOCK' ||
            $rootinBlock.getAttribute('data-type') !== this.getType()
        ) {
            return;
        }

        const $text = Array.from($rootinBlock.children).find(
            (el) => el.tagName === 'ROOTIN-ELEMENT' && el.getAttribute('data-type') === 'text'
        );
        if ($text !== undefined) {
            this.text.$getElement().html($text.innerHTML);
        }
    }

    /**
     * 현재 블록내 선택영역이 존재할 경우, 선택영역을 텍스트로 추출하고 블록에서 제거한다.
     *
     * @param is_all - 선택영역 부분추출이 아닌 전체를 추출할지 여부
     * @returns 추출한 텍스트 목록과, 선택 이후 남은영역이 포함된 블록(없는 경우 NULL)
     */
    async extractSelection(is_all: boolean): Promise<{ texts: string[]; remain: Block | null }> {
        const range = this.editor.selection.getRange();
        if (range === null || range.collapsed === true) {
            return { texts: [], remain: null };
        }

        const $el = this.text.$getElement().getEl();
        if (range.intersectsNode($el) === false) {
            return { texts: [], remain: null };
        }

        let text: string;
        let remain: Block | null = null;

        if (is_all === true) {
            text = this.text.getValue() as string;

            this.text.expectEdit();
            this.text.$getElement().html('');
            this.text.checkSynced();
        } else {
            this.text.expectEdit();

            // 선택영역이 여러 형제블록에 걸쳐있을 수 있으므로(예: 문단A~문단C 드래그), 글로벌
            // range 의 시작/끝을 그대로 쓰지 않고 이 블록 자신의 $el 범위로 좁힌다 — 그렇지 않으면
            // range.extractContents() 가 이 블록 밖(다른 형제블록)까지 통째로 추출해버린다.
            const ownRange = document.createRange();
            if ($el.contains(range.startContainer) === true) {
                ownRange.setStart(range.startContainer, range.startOffset);
            } else {
                ownRange.setStart($el, 0);
            }
            if ($el.contains(range.endContainer) === true) {
                ownRange.setEnd(range.endContainer, range.endOffset);
            } else {
                ownRange.setEnd($el, $el.childNodes.length);
            }

            const afterRange = document.createRange();
            afterRange.setStart(ownRange.endContainer, ownRange.endOffset);
            afterRange.setEnd($el, $el.childNodes.length);
            const afterFragment = afterRange.collapsed === false ? afterRange.extractContents() : null;
            // 선택이 필드의 실제 끝까지 이어져도, 그 뒤에 캐럿 정지용 트레일링 <br> 이 남아있는
            // 경우가 있다(실측 확인) — 그 `<br>` 하나만 잡힌 거라면(텍스트가 전혀 없다면) "뒤에
            // 남는 내용"이 아니므로 remain 을 만들지 않는다.
            const afterContents =
                afterFragment !== null && (afterFragment.textContent ?? '').trim() !== '' ? afterFragment : null;

            const selectedContents = ownRange.extractContents();
            const buffer = document.createElement('div');
            buffer.appendChild(selectedContents);
            text = buffer.innerHTML;

            if (afterContents !== null) {
                const afterBlock = this.createRemainBlock();
                afterBlock.text.$getElement().getEl().appendChild(afterContents);
                remain = afterBlock;
            }

            this.text.checkSynced();
        }

        if (this.isEmpty() === true) {
            await this.getParent()?.removeBlock(this);
        }

        return { texts: [text], remain };
    }

    /**
     * 블록이 삽입되었을 때 이벤트를 처리한다.
     */
    async onInserted(): Promise<void> {
        const range = this.editor.selection.getRange();
        if (range !== null && range.collapsed === false) {
            const container = this.getParent();
            if (container !== null) {
                const texts: string[] = [];
                let remain: Block | null = null;

                for (const sibling of container.getBlocks()) {
                    if (sibling === this) {
                        continue;
                    }

                    const extracted = await sibling.extractSelection(false);
                    texts.push(...extracted.texts);
                    if (extracted.remain !== null) {
                        remain = extracted.remain;
                    }
                }

                if (texts.length > 0) {
                    await this.text.setValue(texts.join('<br>'));
                }

                if (remain !== null) {
                    await container.insertBlock(remain, this.$getBlock().getEl().nextSibling);
                }
            }
        }

        this.text.focus();
    }

    /**
     * 선택영역 추출 후 뒤에 남는 내용을 담을, 나와 같은 타입의 새 블록을 만든다. 하위 클래스가
     * 재정의하여 자신의 생성자 옵션(레벨 등)을 유지한 채로 만든다.
     *
     * @returns 새 블록 인스턴스
     */
    protected createRemainBlock(): Paragraph {
        return new Paragraph(this.editor, { role: this.text.properties.role, placeholder: this.placeholder });
    }

    /**
     * 블록이 포커스를 받았을 때 이벤트를 처리한다.
     *
     * @param element - 캐럿이 위치한 엘리먼트 (NULL 이면 아직 캐럿 위치가 없다는 뜻)
     */
    onFocus(element: Element | null): void {
        if (element === null) {
            this.text.focus();
        }
    }

    /**
     * 블록이 포커스를 받을 가능성이 있을 때 이벤트를 처리한다.
     */
    onFocusable(): void {
        this.onFocus(null);
    }

    /**
     * 블록내에서 Enter 키입력이 발생했을 때 이벤트를 처리한다.
     *
     * @param element - 실제로 Enter 키입력이 발생한 엘리먼트 (NULL 이면 블록자체)
     */
    onEnter(element: Element): void {
        this.getParent().splitBlock(new Paragraph(this.editor), this, element);
    }

    /**
     * 클립보드에서 붙여넣기 하였을 때 이벤트를 처리한다.
     *
     * @param type - 클립보드 타입
     * @param data - 클립보드 데이터
     * @param focused - Element 인 경우 나의 블록의 포커스된 엘리먼트, `Block`인 경우 새 블록을 삽입할 기준블록
     * @returns 처리결과 (true 인 경우 모든 데이터를 처리함, 그렇지 않은 경우 처리되지 않은 데이터)
     */
    async onPaste(type: string, data: any, focused: Block | Element | null = null): Promise<true | any> {
        if (type.startsWith('text/') === false) {
            return data;
        }

        const wrappers = [...Text.genericWrapperTags, `rootin-block[data-type='${this.getType()}']`];

        if (focused instanceof Element) {
            return (await this.text.onPaste(type, data, wrappers)).remain;
        }

        if (focused === null) {
            return data;
        }

        const container = focused.getParent();
        if (container === null) {
            return data;
        }

        const { remain } = await this.text.onPaste(type, data, wrappers);
        if (this.text.isEmpty() === false) {
            await container.insertBlock(this, focused.$getBlock().getEl().nextSibling);
        }

        return remain;
    }
}
